import { useEffect, useState, useSyncExternalStore } from 'react';
import { useEnv } from '../config/env';
import type { HealthStatus } from '../hl/health';
import { healthText } from '../i18n/common';
import { useT } from '../i18n/useT';
import { useHlHealth } from '../hl/react';

function subscribeOnline(cb: () => void): () => void {
  window.addEventListener('online', cb);
  window.addEventListener('offline', cb);
  return () => {
    window.removeEventListener('online', cb);
    window.removeEventListener('offline', cb);
  };
}

/** False only when the browser knows it has no network (then the problem is not Hyperliquid). */
function useBrowserOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
    () => true,
  );
}

export interface HealthBannerViewProps {
  status: HealthStatus;
  network: string;
  online?: boolean;
  onDismiss?: () => void;
}

/** Presentational banner (#771). Degraded = amber + dismiss button; down = red, not dismissible. */
export function HealthBannerView({ status, network, online = true, onDismiss }: HealthBannerViewProps) {
  const t = useT();
  if (status === 'ok') return null;
  const down = status === 'down';
  const text = online
    ? healthText(status, network)
    : t('shell.health.offline');
  return (
    <div
      role={down ? 'alert' : 'status'}
      data-testid="hl-health-banner"
      data-status={status}
      className={`flex items-center justify-center gap-3 px-4 py-2 text-center text-xs font-medium sm:text-sm ${
        down ? 'bg-danger text-bg' : 'bg-warn text-warn-fg'
      }`}
    >
      <span>{text}</span>
      {!down && onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('shell.health.dismiss')}
          data-testid="hl-health-dismiss"
          className="shrink-0 rounded px-1.5 leading-none hover:bg-black/10"
        >
          ×
        </button>
      )}
    </div>
  );
}

/** Site-wide notice under the header for the selected network; clears itself when Hyperliquid is healthy again. */
export default function HealthBanner() {
  const { env } = useEnv();
  const status = useHlHealth();
  const online = useBrowserOnline();
  const [dismissed, setDismissed] = useState(false);
  // A dismissal covers the current degraded spell only: any status change (or network switch) shows it again.
  useEffect(() => setDismissed(false), [status, env.name]);
  if (dismissed && status === 'degraded') return null;
  return (
    <HealthBannerView status={status} network={env.name} online={online} onDismiss={() => setDismissed(true)} />
  );
}
